import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Button,
} from '@mui/material';
import { AiFillCloseCircle } from 'react-icons/ai';
import WizardButton from '../../components/WizardButton';
import WizardTextField from '../../components/WizardTextField';
import IFormError from '../../../../interfaces/IFormError';
import * as yup from 'yup';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';

interface CompanionData {
  companionName: string;
  companionRelationship: string;
  companionAge: number | '';
}

interface CompanionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: CompanionData) => void;
}

const CompanionDialog = ({ isOpen, onClose, onConfirm }: CompanionDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [companionName, setCompanionName] = useState('');
  const [companionRelationship, setCompanionRelationship] = useState('');
  const [companionAge, setCompanionAge] = useState<number | ''>('');
  const [errors, setErrors] = useState<IFormError>();
  const validationProvider = useValidateSchema();

  const schema = yup.object({
    companionName: yup.string(),
    companionRelationship: yup.string(),
    companionAge: yup
      .number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable()
      .when('companionName', {
        is: (val: string) => val && val.trim().length > 0,
        then: schema =>
          schema
            .required('Campo obrigatório')
            .min(1, 'Idade deve ser maior que 0')
            .max(150, 'Idade inválida'),
        otherwise: schema => schema.notRequired(),
      }),
  });

  const handleSubmit = async () => {
    setLoading(true);

    const result = await validationProvider.validate(schema, {
      companionName,
      companionRelationship,
      companionAge,
    });

    setErrors({ ...result.errors });
    if (!result.isValid) {
      setLoading(false);
      return;
    }

    onConfirm({
      companionName,
      companionRelationship,
      companionAge,
    });
    setLoading(false);
  };

  const handleClose = () => {
    setCompanionName('');
    setCompanionRelationship('');
    setCompanionAge('');
    setErrors(undefined);
    onClose();
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <EscalatorWarningIcon color="warning" sx={{ mr: 1 }} />
        Informe os dados do acompanhante
        <IconButton onClick={handleClose} size="small">
          <AiFillCloseCircle />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <WizardTextField
            label="Nome do acompanhante"
            placeholder="Nome completo"
            value={companionName}
            onChange={e => setCompanionName(e.target.value)}
            variant="outlined"
            error={Boolean(errors?.companionName)}
            helperText={errors?.companionName}
            autoFocus
            inputProps={{ maxLength: 100 }}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Parentesco"
            value={companionRelationship}
            onChange={e => setCompanionRelationship(e.target.value)}
            error={Boolean(errors?.companionRelationship)}
            helperText={errors?.companionRelationship}
            variant="outlined"
            fullWidth
          >
            <MenuItem value="Filho(a)">Filho(a)</MenuItem>
            <MenuItem value="Cônjuge">Cônjuge</MenuItem>
            <MenuItem value="Pai/Mãe">Pai/Mãe</MenuItem>
            <MenuItem value="Tio(a)">Tio(a)</MenuItem>
            <MenuItem value="Sobrinho(a)">Sobrinho(a)</MenuItem>
            <MenuItem value="Neto(a)">Neto(a)</MenuItem>
            <MenuItem value="Outro">Outro</MenuItem>
          </TextField>
          <WizardTextField
            label="Idade"
            placeholder="Idade do acompanhante"
            type="number"
            value={companionAge}
            onChange={e => setCompanionAge(e.target.value === '' ? '' : Number(e.target.value))}
            variant="outlined"
            error={Boolean(errors?.companionAge)}
            helperText={errors?.companionAge}
            inputProps={{ min: 1, max: 150 }}
            onInput={e => {
              const target = e.target as HTMLInputElement;
              if (target.value.length > 3) target.value = target.value.slice(0, 3);
            }}
            fullWidth
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ display: 'flex', padding: 2, justifyContent: 'space-between' }}>
        <Button variant="text" sx={{ textTransform: 'none' }} onClick={handleSubmit}>
          Não tenho esses dados
        </Button>
        <WizardButton
          type="button"
          isLoading={loading}
          disabled={loading}
          onClick={handleSubmit}
        >
          CONFIRMAR
        </WizardButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompanionDialog;
